import React, { useState, useEffect } from 'react';
import { customerSegmentApi } from '../services/customerSegmentApi';
import { CustomerSegment, CustomFieldDefinition } from '../types/CustomerSegment';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const CustomerSegmentsManager: React.FC = () => {
  const [segments, setSegments] = useState<CustomerSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSegment, setEditingSegment] = useState<CustomerSegment | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<CustomerSegment | null>(null);
  const [archiveConfirm, setArchiveConfirm] = useState<CustomerSegment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    color: '#3B82F6',
    customFieldsSchema: [] as CustomFieldDefinition[]
  });

  // Custom field editing - now manages multiple fields at once
  const [customFieldRows, setCustomFieldRows] = useState<Array<{
    id: string;
    key: string;
    label: string;
    type: 'text' | 'number' | 'select' | 'multiselect' | 'date' | 'textarea' | 'checkbox';
    value: string;
    options: string[];
    optionInput: string;
  }>>([]);

  useEffect(() => {
    loadSegments();
  }, []);

  const loadSegments = async () => {
    setLoading(true);
    try {
      const response = await customerSegmentApi.getSegments(true); // Include inactive
      const segmentsData = response.data || [];
      
      // Ensure customFieldsSchema is parsed
      const parsedSegments = segmentsData.map(seg => ({
        ...seg,
        customFieldsSchema: typeof seg.customFieldsSchema === 'string' 
          ? JSON.parse(seg.customFieldsSchema) 
          : (seg.customFieldsSchema || [])
      }));
      
      setSegments(parsedSegments);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load customer segments');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (segment?: CustomerSegment) => {
    if (segment) {
      setEditingSegment(segment);
      
      // Parse customFieldsSchema if it's a string
      let parsedCustomFields = [];
      if (segment.customFieldsSchema) {
        if (typeof segment.customFieldsSchema === 'string') {
          try {
            parsedCustomFields = JSON.parse(segment.customFieldsSchema);
          } catch (e) {
            console.error('Error parsing custom fields:', e);
            parsedCustomFields = [];
          }
        } else if (Array.isArray(segment.customFieldsSchema)) {
          parsedCustomFields = segment.customFieldsSchema;
        }
      }
      
      setFormData({
        name: segment.name,
        code: segment.code,
        description: segment.description || '',
        color: segment.color,
        customFieldsSchema: parsedCustomFields
      });
      
      // Convert existing custom fields to rows
      const rows = parsedCustomFields.map((field: any, index: number) => ({
        id: `existing-${index}`,
        key: field.key,
        label: field.label,
        type: field.type,
        value: field.value || '',
        options: field.options || [],
        optionInput: ''
      }));
      setCustomFieldRows(rows);
    } else{
      setEditingSegment(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        color: '#3B82F6',
        customFieldsSchema: []
      });
      setCustomFieldRows([]);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSegment(null);
    setCustomFieldRows([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      // Convert custom field rows to schema format
      const customFieldsSchema = customFieldRows
        .filter(row => row.key && row.label) // Only include rows with key and label
        .map(row => ({
          key: row.key.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
          label: row.label,
          type: row.type,
          value: row.value || null,
          options: ['select', 'multiselect'].includes(row.type) ? row.options : undefined
        }));

      const submitData = {
        ...formData,
        customFieldsSchema
      };
      
      if (editingSegment) {
        await customerSegmentApi.updateSegment(editingSegment.id, submitData);
        setSuccessMessage('Customer segment updated successfully');
      } else {
        await customerSegmentApi.createSegment(submitData);
        setSuccessMessage('Customer segment created successfully');
      }
      
      await loadSegments();
      handleCloseModal();
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error saving segment:', err);
      setError(err.response?.data?.message || 'Failed to save customer segment');
    }
  };

  const handleArchive = async (segment: CustomerSegment) => {
    setArchiveConfirm(null);
    try {
      const response = await customerSegmentApi.archiveSegment(segment.id);
      
      let message = 'Customer segment archived successfully';
      if (response.warnings) {
        if (response.warnings.leadsCount > 0 || response.warnings.stagesCount > 0) {
          message += `. Note: ${response.warnings.leadsCount} lead(s) and ${response.warnings.stagesCount} stage(s) were using this segment.`;
        }
      }
      
      setSuccessMessage(message);
      await loadSegments();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to archive customer segment');
    }
  };

  const handleRestore = async (segment: CustomerSegment) => {
    try {
      await customerSegmentApi.restoreSegment(segment.id);
      setSuccessMessage('Customer segment restored successfully');
      await loadSegments();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to restore customer segment');
    }
  };

  const handleSetDefault = async (segment: CustomerSegment) => {
    try {
      await customerSegmentApi.setDefaultSegment(segment.id);
      setSuccessMessage(`"${segment.name}" set as default segment`);
      await loadSegments();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to set default segment');
    }
  };

  const handleDelete = async (segment: CustomerSegment) => {
    setDeleteConfirm(null);
    try {
      await customerSegmentApi.deleteSegment(segment.id);
      setSuccessMessage('Customer segment deleted successfully');
      await loadSegments();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete customer segment');
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    
    const newSegments = [...segments];
    const temp = newSegments[index];
    newSegments[index] = newSegments[index - 1];
    newSegments[index - 1] = temp;
    
    const orders = newSegments.map((seg, idx) => ({
      id: seg.id,
      orderIndex: idx + 1
    }));
    
    try {
      await customerSegmentApi.reorderSegments({ segmentOrders: orders });
      await loadSegments();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reorder segments');
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === segments.length - 1) return;
    
    const newSegments = [...segments];
    const temp = newSegments[index];
    newSegments[index] = newSegments[index + 1];
    newSegments[index + 1] = temp;
    
    const orders = newSegments.map((seg, idx) => ({
      id: seg.id,
      orderIndex: idx + 1
    }));
    
    try {
      await customerSegmentApi.reorderSegments({ segmentOrders: orders });
      await loadSegments();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reorder segments');
    }
  };

  // Custom field management - New approach
  const addCustomFieldRow = () => {
    setCustomFieldRows([...customFieldRows, {
      id: Date.now().toString(),
      key: '',
      label: '',
      type: 'text',
      value: '',
      options: [],
      optionInput: ''
    }]);
  };

  const removeCustomFieldRow = (id: string) => {
    setCustomFieldRows(customFieldRows.filter(row => row.id !== id));
  };

  const updateCustomFieldRow = (id: string, field: string, value: any) => {
    setCustomFieldRows(customFieldRows.map(row => 
      row.id === id ? { ...row, [field]: value } : row
    ));
  };

  const addOptionToRow = (rowId: string) => {
    const row = customFieldRows.find(r => r.id === rowId);
    if (!row || !row.optionInput.trim()) return;
    
    updateCustomFieldRow(rowId, 'options', [...row.options, row.optionInput.trim()]);
    updateCustomFieldRow(rowId, 'optionInput', '');
  };

  const removeOptionFromRow = (rowId: string, optionIndex: number) => {
    const row = customFieldRows.find(r => r.id === rowId);
    if (!row) return;
    
    updateCustomFieldRow(rowId, 'options', row.options.filter((_, i) => i !== optionIndex));
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">Loading segments...</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Customer Segments</h2>
            <p className="text-gray-600 mt-1">
              Create and manage customer segments for targeted sales pipelines
            </p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add Segment</span>
          </button>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Segments Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leads</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stages</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {segments.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    No customer segments found. Create your first segment to get started.
                  </td>
                </tr>
              ) : (
                segments.map((segment, index) => (
                  <tr key={segment.id} className={!segment.isActive ? 'bg-gray-50 opacity-60' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0 || !segment.isActive}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronUpIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleMoveDown(index)}
                          disabled={index === segments.length - 1 || !segment.isActive}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronDownIcon className="h-4 w-4" />
                        </button>
                        <span className="text-sm text-gray-500 ml-2">{index + 1}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: segment.color }}
                        />
                        <span className="text-sm font-medium text-gray-900">{segment.name}</span>
                        {segment.isDefault && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-mono bg-gray-100 text-gray-700 rounded">
                        {segment.code}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600 line-clamp-2">
                        {segment.description || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                        {segment.leadsCount || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                        {segment.stagesCount || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {segment.isActive ? (
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                          Archived
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {segment.isActive ? (
                          <>
                            {!segment.isDefault && (
                              <button
                                onClick={() => handleSetDefault(segment)}
                                className="text-xs text-green-600 hover:text-green-800 font-medium px-2 py-1 hover:bg-green-50 rounded"
                                title="Set as Default"
                              >
                                Set Default
                              </button>
                            )}
                            <button
                              onClick={() => handleOpenModal(segment)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Edit"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setArchiveConfirm(segment)}
                              className="text-orange-600 hover:text-orange-800"
                              title="Archive"
                            >
                              <ArchiveBoxIcon className="h-5 w-5" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleRestore(segment)}
                              className="text-green-600 hover:text-green-800"
                              title="Restore"
                            >
                              <ArrowPathIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(segment)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete Permanently"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingSegment ? 'Edit Customer Segment' : 'Create Customer Segment'}
                </h2>
                <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Segment Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Undergraduate Students"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                        placeholder="e.g., UG"
                        pattern="[A-Z0-9_-]+"
                        title="Only uppercase letters, numbers, dashes, and underscores"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Uppercase letters, numbers, dashes, underscores only</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Describe this customer segment..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                      />
                      <span className="text-sm text-gray-600">{formData.color}</span>
                    </div>
                  </div>
                </div>

                {/* Custom Fields */}
                <div className="space-y-4 border-t pt-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">Custom Fields (Optional)</h3>
                    <button
                      type="button"
                      onClick={addCustomFieldRow}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-1"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Add Field</span>
                    </button>
                  </div>
                  
                  {customFieldRows.length === 0 ? (
                    <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
                      <p className="text-sm text-gray-500">No custom fields yet</p>
                      <p className="text-xs text-gray-400 mt-1">Click "Add Field" to create custom fields for this segment</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {customFieldRows.map((row, index) => (
                        <div key={row.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                          <div className="flex items-start justify-between mb-3">
                            <span className="text-xs font-semibold text-gray-600">Field #{index + 1}</span>
                            <button
                              type="button"
                              onClick={() => removeCustomFieldRow(row.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-2 mb-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Key *</label>
                              <input
                                type="text"
                                value={row.key}
                                onChange={(e) => updateCustomFieldRow(row.id, 'key', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                placeholder="duration"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Label *</label>
                              <input
                                type="text"
                                value={row.label}
                                onChange={(e) => updateCustomFieldRow(row.id, 'label', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                placeholder="Program Duration"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Type</label>
                              <select
                                value={row.type}
                                onChange={(e) => updateCustomFieldRow(row.id, 'type', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              >
                                <option value="text">Text</option>
                                <option value="number">Number</option>
                                <option value="select">Select</option>
                                <option value="multiselect">Multi-Select</option>
                                <option value="date">Date</option>
                                <option value="textarea">Text Area</option>
                              </select>
                            </div>
                          </div>
                          
                          {/* Options for select types */}
                          {['select', 'multiselect'].includes(row.type) && (
                            <div className="mb-2">
                              <label className="block text-xs text-gray-600 mb-1">Options</label>
                              <div className="flex space-x-2">
                                <input
                                  type="text"
                                  value={row.optionInput}
                                  onChange={(e) => updateCustomFieldRow(row.id, 'optionInput', e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOptionToRow(row.id))}
                                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                                  placeholder="Press Enter to add"
                                />
                                <button
                                  type="button"
                                  onClick={() => addOptionToRow(row.id)}
                                  className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                                >
                                  Add
                                </button>
                              </div>
                              {row.options.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {row.options.map((option, idx) => (
                                    <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                                      {option}
                                      <button type="button" onClick={() => removeOptionFromRow(row.id, idx)} className="ml-1">×</button>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Default Value */}
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Default Value</label>
                            <input
                              type={row.type === 'number' ? 'number' : 'text'}
                              value={row.value}
                              onChange={(e) => updateCustomFieldRow(row.id, 'value', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              placeholder="Optional"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                    💡 <strong>Tip:</strong> Custom fields are saved when you click "Create Segment" or "Update Segment". No need to click "Add" for each field.
                  </p>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingSegment ? 'Update Segment' : 'Create Segment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Archive Confirmation Modal */}
        {archiveConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center space-x-3 text-orange-600 mb-4">
                <ExclamationTriangleIcon className="h-6 w-6" />
                <h3 className="text-lg font-semibold">Archive Segment?</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Are you sure you want to archive "<strong>{archiveConfirm.name}</strong>"?
              </p>
              {(archiveConfirm.leadsCount || 0) > 0 && (
                <p className="text-sm text-orange-600 mb-4">
                  ⚠️ This segment is currently used by {archiveConfirm.leadsCount} lead(s).
                </p>
              )}
              {(archiveConfirm.stagesCount || 0) > 0 && (
                <p className="text-sm text-orange-600 mb-4">
                  ⚠️ This segment has {archiveConfirm.stagesCount} assigned stage(s).
                </p>
              )}
              <p className="text-sm text-gray-500 mb-6">
                The segment will be hidden but existing data will be preserved.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setArchiveConfirm(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleArchive(archiveConfirm)}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  Archive Segment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center space-x-3 text-red-600 mb-4">
                <ExclamationTriangleIcon className="h-6 w-6" />
                <h3 className="text-lg font-semibold">Delete Segment Permanently?</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Are you sure you want to permanently delete "<strong>{deleteConfirm.name}</strong>"?
              </p>
              <p className="text-sm text-red-600 mb-6">
                ⚠️ This action cannot be undone. This will only work if no leads or stages are using this segment.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CustomerSegmentsManager;
